// src/views/pms/RoomBooking.jsx
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../../../../api/axios";
import BackButton from "../BackButton";

const RoomBooking = () => {
  // ----- data lists -----
  const [roomTypes, setRoomTypes] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [roomsForType, setRoomsForType] = useState([]);
  const [selectedRoomDetails, setSelectedRoomDetails] = useState(null);
  const [rateTypes, setRateTypes] = useState([]);
  const [filteredRateTypes, setFilteredRateTypes] = useState([]);
  const [guestSuggestions, setGuestSuggestions] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [loading, setLoading] = useState(false);
  const initialState = {
    check_in: "",
    check_out: "",
    arrival_from: "",
    booking_type: "",
    booking_reference_no: "",
    purpose_of_visit: "",
    remarks: "",

    // room selection
    room_type_id: "",
    room_id: "",
    adults: 1,
    children: 0,

    // guest flattened (used by your ReservationService)
    title: "",
    name: "",
    father_name: "",
    mobile_no: "",
    email: "",
    nationality: "",
    gender: "",
    occupation: "",
    dob: "",
    anniversary: "",
    is_vip: false,

    // contact (prefixed fields)
    contact_type: "",
    contact_country: "",
    contact_state: "",
    contact_city: "",
    contact_zipcode: "",
    contact_address: "",

    // identity & files
    identity_type: "",
    identity_no: "",
    identity_comments: "",
    front_doc: null,
    back_doc: null,
    guest_image: null,

    // financials
    booking_charge: "",
    discount_percent: "",
    discount_reason: "",
    commission_percent: "",
    commission_amount: "",
    service_charge: "",
    paid_amount: "",
    total: "",
    payment_mode: "",
    advance_amount: "",
    advance_remarks: "",
    // PREVIEW STATES
    front_doc_preview: "",
    back_doc_preview: "",
    guest_image_preview: "",

    // meta
    source_of_booking: "",
    status: "reserved",
  };
  // ----- full form state matching Reservation model + guest flattened fields -----
  const [form, setForm] = useState({
    initialState,
    // reservation basics
  });

  // ---------- Load room types and rooms ----------
  useEffect(() => {
    loadRoomTypes();
    loadAllRooms();
    loadRateTypes();
    loadTaxes();
  }, []);

  const loadTaxes = async () => {
    try {
      const res = await api.get("/pms/taxes");
      const taxes = res?.data.filter((t) => t.type === "room");
      setTaxes(taxes);
    } catch (err) {
      console.error("Failed to load taxes", err);
    }
  };

  const loadRateTypes = async (roomTypeId) => {
    try {
      const res = await api.get("/pms/rate-types");
      setRateTypes(res.data || []);
    } catch (err) {
      console.error("Failed to load rate types", err);
    }
  };

  const loadRoomTypes = async () => {
    try {
      const res = await api.get("/pms/room-types", {});
      setRoomTypes(res.data || []);
    } catch (err) {
      console.error("Failed to load room types:", err);
    }
  };

  const loadAllRooms = async () => {
    try {
      const res = await api.get("/pms/rooms", {});
      setAllRooms(res.data || []);
    } catch (err) {
      console.error("Failed to load rooms:", err);
    }
  };

  // ---------- Filter rooms when room_type_id changes ----------
  useEffect(() => {
    if (!form.room_type_id) {
      setRoomsForType([]);
      return;
    }

    const filtered = allRooms.filter(
      (r) => String(r.room_type_id) === String(form.room_type_id)
    );

    setRoomsForType(filtered);

    if (!filtered.find((r) => String(r.id) === String(form.room_id))) {
      updateField("room_id", "");
      setSelectedRoomDetails(null);
    }
  }, [form.room_type_id, allRooms]);

  useEffect(() => {
    if (!form.room_type_id) {
      setRoomsForType([]);
      return;
    }

    // STEP 1: Find selected room type NAME
    const selectedRoomTypeName = roomTypes.find(
      (t) => String(t.id) === String(form.room_type_id)
    )?.name;

    // STEP 2: Filter rooms where room.type matches selected NAME
    const filtered = allRooms.filter(
      (r) => r.type?.toLowerCase() === selectedRoomTypeName?.toLowerCase()
    );

    setRoomsForType(filtered);

    if (!filtered.find((r) => String(r.id) === String(form.room_id))) {
      updateField("room_id", "");
      setSelectedRoomDetails(null);
      setFilteredRateTypes([]);
    }
  }, [form.room_type_id, allRooms, roomTypes]);

  useEffect(() => {
    if (!form.room_id) {
      setSelectedRoomDetails(null);
      setFilteredRateTypes([]);
      return;
    }

    const room = allRooms.find((r) => String(r.id) === String(form.room_id));
    if (!room) return;

    setSelectedRoomDetails(room);

    // Filter rate types using name match
    const matched = rateTypes.filter(
      (rt) => rt.room_type?.name?.toLowerCase() === room.type?.toLowerCase()
    );

    setFilteredRateTypes(matched);

    setForm((p) => ({
      ...p,
      booking_charge: p.booking_charge || room.room_price || "",
    }));
  }, [form.room_id, rateTypes, allRooms]);

  // ---------- helpers ----------
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateNested = (sectionKey, value) => {
    // for guest/contact flattened we simply set top-level keys; kept for semantic use
    updateField(sectionKey, value);
  };

  const onFileChange = (key, file) => {
    setForm((prev) => ({ ...prev, [key]: file }));
  };

  const buildFormData = (payload) => {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      // files
      if (v instanceof File) {
        fd.append(k, v);
        return;
      }
      // booleans -> convert to 1/0 for backend
      if (typeof v === "boolean") {
        fd.append(k, v ? 1 : 0);
        return;
      }
      // arrays/objects -> JSON stringify (not used here)
      if (typeof v === "object") {
        fd.append(k, JSON.stringify(v));
        return;
      }
      fd.append(k, String(v));
    });
    return fd;
  };

  // ---------- Basic client validation ----------
  const validate = () => {
    if (!form.check_in || !form.check_out) {
      Swal.fire(
        "Validation",
        "Please provide check-in and check-out dates",
        "warning"
      );
      return false;
    }
    if (!form.room_type_id) {
      Swal.fire("Validation", "Please select room type", "warning");
      return false;
    }
    if (!form.room_id) {
      Swal.fire("Validation", "Please select room number", "warning");
      return false;
    }
    if (!form.name || !form.mobile_no) {
      Swal.fire(
        "Validation",
        "Guest name and mobile number are required",
        "warning"
      );
      return false;
    }
    return true;
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        property_code: form.property_code,
        room_id: form.room_id,
        rate_type_id: form.rate_type_id,
        check_in: form.check_in,
        check_out: form.check_out,
        arrival_from: form.arrival_from,
        booking_type: form.booking_type,
        booking_reference_no: form.booking_reference_no,
        purpose_of_visit: form.purpose_of_visit,
        remarks: form.remarks,
        adults: Number(form.adults || 0),
        children: Number(form.children || 0),
        title: form.title,
        name: form.name,
        father_name: form.father_name,
        mobile_no: form.mobile_no,
        email: form.email,
        nationality: form.nationality,
        gender: form.gender,
        occupation: form.occupation,
        dob: form.dob,
        anniversary: form.anniversary,
        is_vip: form.is_vip ? 1 : 0,
        contact_type: form.contact_type,
        contact_country: form.contact_country,
        contact_state: form.contact_state,
        contact_city: form.contact_city,
        contact_zipcode: form.contact_zipcode,
        contact_address: form.contact_address,
        identity_type: form.identity_type,
        identity_no: form.identity_no,
        identity_comments: form.identity_comments,
        front_doc: form.front_doc,
        back_doc: form.back_doc,
        guest_image: form.guest_image,
        booking_charge: form.booking_charge,
        discount_percent: form.discount_percent,
        discount_reason: form.discount_reason,
        commission_percent: form.commission_percent,
        commission_amount: form.commission_amount,
        service_charge: form.service_charge,
        paid_amount: form.paid_amount,
        total: form.total,
        payment_mode: form.payment_mode,
        advance_amount: form.advance_amount,
        advance_remarks: form.advance_remarks,
        source_of_booking: form.source_of_booking,
        status: form.status,
      };

      const fd = buildFormData(payload);

      const res = await api.post("/pms/reservations", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire(
        "Success",
        res.data?.message || "Reservation created",
        "success"
      );
      // optional: reset or redirect
      setForm(initialState);
      // if you want to clear
    } catch (err) {
      console.error("Failed to create reservation:", err);
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Failed to create reservation",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const calculatePrice = () => {
      if (!form.room_id || !form.check_in || !form.check_out) return;

      const ci = new Date(form.check_in);
      const co = new Date(form.check_out);
      const nights = (co - ci) / (1000 * 60 * 60 * 24);
      if (nights <= 0) return;

      const room = allRooms.find((r) => r.id == form.room_id);
      const rate = filteredRateTypes.find((rt) => rt.id == form.rate_type_id);

      const roomPrice = Number(room?.room_price ?? 0);
      const ratePrice = Number(rate?.base_price ?? 0);
      const bookingCharge = roomPrice + ratePrice;

      const subTotal = bookingCharge * nights;

      let taxAmount = 0;
      taxes.forEach((t) => {
        taxAmount += (subTotal * t.percentage) / 100;
      });

      const total = subTotal + taxAmount + (Number(form.service_charge) || 0);

      updateField("sub_total", subTotal.toFixed(2));
      updateField("nights", nights);
      updateField("booking_charge", bookingCharge.toFixed(2));
      updateField("tax", taxAmount.toFixed(2));
      updateField("total", total.toFixed(2));
    };

    calculatePrice();
  }, [
    form.room_id,
    form.rate_type_id,
    form.check_in,
    form.check_out,
    form.service_charge,
    allRooms,
    filteredRateTypes,
  ]);

  const searchGuest = async (query) => {
    if (!query || query.length < 2) {
      setGuestSuggestions([]);
      return;
    }

    try {
      const res = await api.get(`/pms/guests/search?query=${query}`);
      if (res.data) {
        setGuestSuggestions(res.data);
      } else {
        setGuestSuggestions([]);
      }
    } catch (err) {
      setGuestSuggestions([]);
    }
  };

  const selectGuest = (g) => {
    setGuestSuggestions([]);

    setForm((prev) => ({
      ...prev,

      // basic
      name: g.name || "",
      mobile_no: g.mobile_no || "",
      email: g.email || "",
      title: g.title || "",
      gender: g.gender || "",
      father_name: g.father_name || "",
      dob: g.dob || "",
      anniversary: g.anniversary || "",
      occupation: g.occupation || "",
      is_vip: g.is_vip ? true : false,

      // contact
      contact_address: g.address || "",
      contact_city: g.city || "",
      contact_state: g.state || "",
      contact_zipcode: g.zipcode || "",

      // identity
      identity_type: g.identity_type || "",
      identity_no: g.identity_no || "",
      identity_comments: g.identity_comments || "",

      front_doc_preview: g.front_doc_url || "",
      back_doc_preview: g.back_doc_url || "",
      guest_image_preview: g.guest_image_url || "",
    }));
  };

  // ---------- UI: same design you provided, bound to state ----------
  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-sm-12">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <h3 className="fw-bold mb-0">Fill Out Booking Details</h3>
            </div>
          </div>
          <div className="col-sm-12">
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title">Reservation Details</h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Check In <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-calendar-plus"></i>
                        </span>
                        <input
                          type="date"
                          className="form-control"
                          value={form.check_in}
                          min={new Date().toISOString().split("T")[0]}
                          onChange={(e) =>
                            updateField("check_in", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Check Out <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-calendar-minus"></i>
                        </span>
                        <input
                          type="date"
                          className="form-control"
                          min={
                            form.check_in
                              ? new Date(
                                  new Date(form.check_in).setDate(
                                    new Date(form.check_in).getDate() + 1
                                  )
                                )
                                  .toISOString()
                                  .split("T")[0]
                              : new Date().toISOString().split("T")[0]
                          }
                          value={form.check_out}
                          onChange={(e) =>
                            updateField("check_out", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Arrival From
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-stop-circle"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Arrival From"
                          value={form.arrival_from}
                          onChange={(e) =>
                            updateField("arrival_from", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Booking Type
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-house"></i>
                        </span>
                        <select
                          className="form-select"
                          value={form.booking_type}
                          onChange={(e) =>
                            updateField("booking_type", e.target.value)
                          }
                        >
                          <option value="">Choose Booking Reference</option>
                          <option>Walk-in</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Booking Reference No
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-bookmark-heart"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Booking Reference No."
                          value={form.booking_reference_no}
                          onChange={(e) =>
                            updateField("booking_reference_no", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Purpose of Visit
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-eyeglasses"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Purpose of Visit"
                          value={form.purpose_of_visit}
                          onChange={(e) =>
                            updateField("purpose_of_visit", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12 col-lg-12 col-xl-12">
                    <div className="form-group">
                      <label className="form-label text-muted">Remarks</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-bookmark-star"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Remarks"
                          value={form.remarks}
                          onChange={(e) =>
                            updateField("remarks", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Room Details */}
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title">Room Details</h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Room Type <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-house"></i>
                        </span>
                        <select
                          className="form-select"
                          value={form.room_type_id}
                          onChange={(e) =>
                            updateField("room_type_id", e.target.value)
                          }
                        >
                          <option value="">Choose Room Type</option>
                          {roomTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Room No <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-house"></i>
                        </span>
                        <select
                          className="form-select"
                          value={form.room_id}
                          onChange={(e) =>
                            updateField("room_id", e.target.value)
                          }
                        >
                          <option value="">Choose Room No</option>
                          {roomsForType.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.room_number}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">
                        Rate Type <span className="text-danger">*</span>
                      </label>

                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-cash"></i>
                        </span>

                        <select
                          className="form-select"
                          value={form.rate_type_id}
                          onChange={(e) =>
                            updateField("rate_type_id", e.target.value)
                          }
                        >
                          <option value="">Choose Rate Type</option>

                          {filteredRateTypes.map((rt) => (
                            <option key={rt.id} value={rt.id}>
                              {rt.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">Adults</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-people"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          value={form.adults}
                          onChange={(e) =>
                            updateField("adults", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-4">
                    <div className="form-group">
                      <label className="form-label text-muted">Children</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-person"></i>
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          value={form.children}
                          onChange={(e) =>
                            updateField("children", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {form.rate_type_id && (
                    <div className="col-12">
                      <div className="alert alert-light border">
                        <strong>Room Info:</strong>
                        <br />

                        <div>
                          <strong>Room No:</strong>{" "}
                          {selectedRoomDetails.room_number}
                        </div>

                        <div>
                          <strong>Type:</strong>{" "}
                          {selectedRoomDetails.type || "-"}
                        </div>

                        {/* <div>
                          <strong>Room Price:</strong> ₹
                          {selectedRoomDetails.room_price || 0}
                        </div> */}

                        <div>
                          <strong>Capacity:</strong>{" "}
                          {selectedRoomDetails.capacity || "-"}
                        </div>

                        <div>
                          <strong>Room Traffic:</strong>{" "}
                          {
                            filteredRateTypes.find(
                              (r) => r.id == form.rate_type_id
                            )?.name
                          }
                        </div>

                        <div className="mt-2">
                          <strong>Total (Per Night):</strong>{" "}
                          <span className="badge bg-primary fs-6">
                            ₹
                            {(
                              Number(selectedRoomDetails.room_price || 0) +
                              Number(
                                filteredRateTypes.find(
                                  (r) => r.id == form.rate_type_id
                                )?.base_price || 0
                              )
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="card">
              <div className="card-header">
                <h6 className="card-title">Customer Details</h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6 d-flex">
                    <div className="card flex-fill w-100 border">
                      <div className="card-header py-3">
                        <h6 className="card-title">Guest Details</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="form-label text-muted">
                                Title
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <i className="bi bi-emoji-smile"></i>
                                </span>
                                <select
                                  className="form-select"
                                  value={form.title}
                                  onChange={(e) =>
                                    updateField("title", e.target.value)
                                  }
                                >
                                  <option value="">Select</option>
                                  <option value="Mr">Mr</option>
                                  <option value="Ms">Ms</option>
                                  <option value="Mrs">Mrs.</option>
                                  <option value="Dr">Dr</option>
                                  <option value="Engineer">Engineer</option>
                                </select>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="form-label text-muted">
                                Name
                                <span className="text-danger">*</span>
                              </label>
                              <div className="dropdown w-100 position-relative">
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="bi bi-person-circle"></i>
                                  </span>

                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Name"
                                    value={form.name}
                                    onChange={(e) => {
                                      updateField("name", e.target.value);
                                      searchGuest(e.target.value);
                                    }}
                                    autoComplete="off"
                                  />
                                </div>

                                {guestSuggestions.length > 0 && (
                                  <div
                                    className="dropdown-menu show w-100 mt-1"
                                    style={{
                                      position: "absolute",
                                      zIndex: 2000,
                                      maxHeight: "220px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {guestSuggestions.map((g) => (
                                      <button
                                        key={g.id}
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => selectGuest(g)}
                                      >
                                        <strong>{g.name}</strong> —{" "}
                                        {g.mobile_no}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="form-label text-muted">
                                Mobile No.
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">
                                  <i className="bi bi-phone"></i>
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Mobile No."
                                  value={form.mobile_no}
                                  onChange={(e) =>
                                    updateField("mobile_no", e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="form-label text-muted">
                                Email.
                              </label>
                              <div className="input-group">
                                <input
                                  type="email"
                                  className="form-control"
                                  value={form.email}
                                  onChange={(e) =>
                                    updateField("email", e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6 align-self-center">
                            <label className="form-label text-muted w-100">
                              Gender
                            </label>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="gender"
                                id="genderMale"
                                value="Male"
                                checked={form.gender === "male"}
                                onChange={() => updateField("gender", "male")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="genderMale"
                              >
                                Male
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="gender"
                                id="genderFemale"
                                value="Female"
                                checked={form.gender === "female"}
                                onChange={() => updateField("gender", "female")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="genderFemale"
                              >
                                Female
                              </label>
                            </div>
                          </div>

                          <div className="col-md-6 align-self-center mb-3">
                            <label className="form-label text-muted">
                              VIP?
                            </label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={form.is_vip}
                                onChange={(e) =>
                                  updateField("is_vip", e.target.checked)
                                }
                              />
                              <label className="form-check-label">VIP?</label>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-md-12">
                                <label className="form-label text-muted">
                                  Customer Image
                                </label>
                                <input
                                  type="file"
                                  className="form-control"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    onFileChange("guest_image", file);
                                    updateField(
                                      "guest_image_preview",
                                      URL.createObjectURL(file)
                                    );
                                  }}
                                />
                                {form.guest_image_preview && (
                                  <img
                                    src={form.guest_image_preview}
                                    className="mt-2 rounded border"
                                    style={{
                                      width: 70,
                                      height: 70,
                                      objectFit: "cover",
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Identity details */}
                  <div className="col-md-6 d-flex">
                    <div className="card flex-fill w-100 border">
                      <div className="card-header py-3">
                        <h6 className="card-title">Identity Details</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              Identity Type
                            </label>
                            <select
                              className="form-select"
                              value={form.identity_type}
                              onChange={(e) =>
                                updateField("identity_type", e.target.value)
                              }
                            >
                              <option value="">Select ID Type</option>
                              <option value="Aadhaar Card">Aadhaar Card</option>
                              <option value="Passport">Passport</option>
                              <option value="Driving License">
                                Driving License
                              </option>
                              <option value="Voter ID">Voter ID</option>
                              <option value="PAN Card">PAN Card</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              ID # <span className="text-danger">*</span>
                            </label>
                            <input
                              className="form-control"
                              value={form.identity_no}
                              onChange={(e) =>
                                updateField("identity_no", e.target.value)
                              }
                            />
                          </div>

                          <div className="col-md-12">
                            <label>Identity Upload</label>
                          </div>

                          <div className="col-md-12">
                            <label className="form-label text-muted">
                              Front Side Document
                            </label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                onFileChange("front_doc", file);
                                updateField(
                                  "front_doc_preview",
                                  URL.createObjectURL(file)
                                );
                              }}
                            />
                            {form.front_doc_preview && (
                              <img
                                src={form.front_doc_preview}
                                className="mt-2 rounded border"
                                style={{
                                  width: 70,
                                  height: 70,
                                  objectFit: "cover",
                                }}
                              />
                            )}
                          </div>

                          <div className="col-md-12">
                            <label className="form-label text-muted">
                              Back Side Document
                            </label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                onFileChange("back_doc", file);
                                updateField(
                                  "back_doc_preview",
                                  URL.createObjectURL(file)
                                );
                              }}
                            />
                            {form.back_doc_preview && (
                              <img
                                src={form.back_doc_preview}
                                className="mt-2 rounded border"
                                style={{
                                  width: 70,
                                  height: 70,
                                  objectFit: "cover",
                                }}
                              />
                            )}
                          </div>

                          <div className="col-md-12">
                            <label className="form-label text-muted">
                              Comments
                            </label>
                            <textarea
                              className="form-control"
                              value={form.identity_comments}
                              onChange={(e) =>
                                updateField("identity_comments", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>{" "}
          {/* Billing Details */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title">Billing Details</h6>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      <tr>
                        <th>Booking Charge (Per Night)</th>
                        <td>{form.booking_charge || 0}</td>
                      </tr>

                      <tr>
                        <th>Nights</th>
                        <td>{form.nights || 0}</td>
                      </tr>

                      <tr>
                        <th scope="row">Sub Total</th>
                        <td>{form.sub_total || "—"}</td>
                      </tr>
                      <tr>
                        <th scope="row">Tax</th>
                        <td>{form.tax || "—"}</td>
                      </tr>
                      <tr>
                        <th scope="row">Total</th>
                        <td>
                          <span className="badge bg-primary">
                            {form.total || "—"}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-12 text-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Details"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RoomBooking;
